import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CLUBS_DEFAULT,
  DTN_SKIP_KEY,
  GATE_CHECKS,
  KEY_TASKS_FLAT,
  STORAGE_KEY,
  getTaskScript,
  PRESET_CITY_CHIPS,
} from "../constants/activationConsoleData";

function defaultState() {
  return {
    city: "nyc",
    cities: {},
    kpis: { mentions: 0, clubs: 0, sharing: 0, travelers: 0 },
    tasks: {},
    signals: [],
    frictions: [],
    clubs: [],
    clubDensity: {},
    fieldChecks: {},
    gateChecks: {},
    lastSignalDate: null,
  };
}

function loadParsed() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return { ...defaultState(), ...p };
  } catch {
    return null;
  }
}

function normalizeState(s) {
  const base = { ...defaultState(), ...s };
  if (!base.frictions) base.frictions = [];
  if (!base.gateChecks) base.gateChecks = {};
  if (!base.clubs || base.clubs.length === 0) {
    base.clubs = CLUBS_DEFAULT.map((c) => ({ ...c }));
  }
  if (!base.clubDensity) base.clubDensity = {};
  if (!base.fieldChecks) base.fieldChecks = {};
  return base;
}

function loadDtnSkipped() {
  try {
    const raw = localStorage.getItem(DTN_SKIP_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function saveDtnSkipped(set) {
  try {
    localStorage.setItem(DTN_SKIP_KEY, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

export function useActivationConsoleState() {
  const [state, setState] = useState(() => {
    const loaded = loadParsed();
    return normalizeState(loaded || defaultState());
  });
  const [dtnSkipped, setDtnSkipped] = useState(loadDtnSkipped);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* ignore */
    }
  }, [state]);

  const setCity = useCallback((cityKey) => {
    setState((prev) => ({ ...prev, city: cityKey }));
  }, []);

  const duplicateCity = useCallback((name, key) => {
    const k = key.trim().toLowerCase().replace(/\s+/g, "");
    if (!name.trim() || !k) return { ok: false, error: "Fill in both fields." };
    setState((prev) => ({
      ...prev,
      cities: {
        ...prev.cities,
        [k]: {
          name: name.trim(),
          kpis: { mentions: 0, clubs: 0, sharing: 0, travelers: 0 },
          tasks: {},
          signals: [],
          clubs: [],
          clubDensity: {},
        },
      },
    }));
    return { ok: true, key: k };
  }, []);

  const toggleTask = useCallback((id) => {
    setState((prev) => ({
      ...prev,
      tasks: { ...prev.tasks, [id]: !prev.tasks[id] },
    }));
  }, []);

  const dtnMarkDone = useCallback(
    (id) => {
      setState((prev) => ({
        ...prev,
        tasks: { ...prev.tasks, [id]: true },
      }));
      setDtnSkipped((prev) => {
        const next = new Set(prev);
        next.delete(id);
        saveDtnSkipped(next);
        return next;
      });
    },
    []
  );

  const dtnSkip = useCallback((id) => {
    setDtnSkipped((prev) => {
      const next = new Set(prev);
      next.add(id);
      saveDtnSkipped(next);
      return next;
    });
  }, []);

  const toggleFieldCheck = useCallback((id) => {
    setState((prev) => ({
      ...prev,
      fieldChecks: { ...prev.fieldChecks, [id]: !prev.fieldChecks[id] },
    }));
  }, []);

  const toggleGateCheck = useCallback((id) => {
    setState((prev) => ({
      ...prev,
      gateChecks: { ...prev.gateChecks, [id]: !prev.gateChecks[id] },
    }));
  }, []);

  const qrTestPass = useCallback(() => {
    setState((prev) => ({
      ...prev,
      gateChecks: { ...prev.gateChecks, g1: true },
    }));
  }, []);

  const addSignal = useCallback((text, type, source) => {
    const d = new Date();
    setState((prev) => ({
      ...prev,
      signals: [
        ...prev.signals,
        {
          text,
          type,
          source,
          date: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        },
      ],
      lastSignalDate: d.toISOString(),
    }));
  }, []);

  const addFriction = useCallback((text) => {
    const d = new Date();
    setState((prev) => ({
      ...prev,
      frictions: [
        ...prev.frictions,
        {
          text,
          date: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        },
      ],
    }));
  }, []);

  const updateClubStatus = useCallback((clubId, status) => {
    setState((prev) => ({
      ...prev,
      clubs: prev.clubs.map((c) => (c.id === clubId ? { ...c, status } : c)),
    }));
  }, []);

  const toggleDensity = useCallback((clubId, signalId) => {
    setState((prev) => {
      const prevD = prev.clubDensity[clubId] || {};
      return {
        ...prev,
        clubDensity: {
          ...prev.clubDensity,
          [clubId]: { ...prevD, [signalId]: !prevD[signalId] },
        },
      };
    });
  }, []);

  const addClub = useCallback((club) => {
    setState((prev) => ({
      ...prev,
      clubs: [...prev.clubs, { ...club, id: `c${Date.now()}` }],
    }));
  }, []);

  const updateKpi = useCallback((key, val) => {
    const n = Number.parseInt(String(val), 10);
    const num = Number.isFinite(n) && n >= 0 ? n : 0;
    setState((prev) => ({
      ...prev,
      kpis: { ...prev.kpis, [key]: num },
    }));
  }, []);

  const exportJson = useCallback(() => {
    const json = JSON.stringify(state, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `runcards_activation_${state.city}_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [state]);

  const activeClubCount = useMemo(
    () => state.clubs.filter((c) => c.status === "anchor" || c.status === "active").length,
    [state.clubs]
  );

  const handshakeProgress = useMemo(
    () => state.clubs.filter((c) => c.status !== "pending").length,
    [state.clubs]
  );

  const derived = useMemo(() => {
    const gateDone = GATE_CHECKS.filter((g) => state.gateChecks[g.id]).length;
    const gateTotal = GATE_CHECKS.length;
    const gateCleared = gateDone === gateTotal;

    let dtnTask = null;
    for (const t of KEY_TASKS_FLAT) {
      if (!state.tasks[t.id] && !dtnSkipped.has(t.id)) {
        dtnTask = t;
        break;
      }
    }
    if (!dtnTask) {
      for (const t of KEY_TASKS_FLAT) {
        if (!state.tasks[t.id]) {
          dtnTask = t;
          break;
        }
      }
    }

    const dtnDoneCount = KEY_TASKS_FLAT.filter((t) => state.tasks[t.id]).length;
    const dtnProgressPct =
      KEY_TASKS_FLAT.length === 0 ? 0 : Math.round((dtnDoneCount / KEY_TASKS_FLAT.length) * 100);

    const showNoSignalWarning =
      state.signals.length === 0
        ? true
        : state.lastSignalDate
          ? (Date.now() - new Date(state.lastSignalDate).getTime()) / 86400000 >= 3
          : false;

    const isNyc = state.city === "nyc";
    const cityActive = !!state.cities[state.city];
    const phaseLabel = isNyc
      ? "Phase 1 — Seeding"
      : cityActive
        ? "Active"
        : "Not Started";
    const phaseOk = isNyc || cityActive;

    return {
      gateDone,
      gateTotal,
      gateCleared,
      dtnTask,
      dtnDoneCount,
      dtnProgressPct,
      showNoSignalWarning,
      phaseLabel,
      phaseOk,
      activeClubCount,
      handshakeProgress,
    };
  }, [state, dtnSkipped, activeClubCount, handshakeProgress]);

  const presetKeys = useMemo(() => new Set(PRESET_CITY_CHIPS.map((c) => c.key)), []);
  const extraCityKeys = useMemo(
    () => Object.keys(state.cities).filter((k) => !presetKeys.has(k)),
    [state.cities, presetKeys]
  );

  return {
    state,
    dtnSkipped,
    setCity,
    duplicateCity,
    toggleTask,
    dtnMarkDone,
    dtnSkip,
    toggleFieldCheck,
    toggleGateCheck,
    qrTestPass,
    addSignal,
    addFriction,
    updateClubStatus,
    toggleDensity,
    addClub,
    updateKpi,
    exportJson,
    derived,
    presetKeys,
    extraCityKeys,
    getTaskScript,
  };
}
